import { useHistory, useLocation } from 'react-router-dom';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Table, Tag, Typography } from 'antd';
import styled from 'styled-components';

import { SharedTable } from '../../../shared/table';
import type { Event } from '../../../../services/bb-api/event';
import { formatDate } from '../../../../utils/date';
import { txt } from '../../../../locales/i18n';

export type EventOverviewProps = {
	dataSource: Event[];
	loading: boolean;
};

export const EventOverviewDesktop = (props: EventOverviewProps) => {
	const history = useHistory();
	const location = useLocation();

	const renderMunicipalities = (municipalities: { name: string }[] | null) => {
		if (municipalities === null) return null;
		return municipalities.map(({ name }) => <Tag color="blue">{name}</Tag>);
	};

	const renderDurationDates = (event: Event) => {
		const dateStart = new Date(event.dateStart);
		const dateEnd = new Date(event.dateEnd);

		return (
			<div className="duration-container">
				<span>{formatDate(dateStart)}</span>
				<ArrowRightOutlined />
				<span>{formatDate(dateEnd)}</span>
			</div>
		);
	};

	const renderTextContent = (event: Event) => (
		<>
			<Typography.Title level={5} ellipsis>
				{event.title}
			</Typography.Title>
			<Typography.Paragraph className="body-content" ellipsis={{ rows: 2 }}>
				{event.body}
			</Typography.Paragraph>
		</>
	);

	const handleEventNavigation = (eventId: number) => {
		history.replace({ hash: String(eventId), search: location.search });
		history.push(`/events/${eventId}`);
	};

	return (
		<TableStyled
			rowKey="eventId"
			loading={props.loading}
			dataSource={props.dataSource}
			onRow={(record: { eventId: number }) => ({
				onClick: () => handleEventNavigation(record.eventId),
			})}
		>
			<Table.Column
				dataIndex="created"
				title={txt('global.created')}
				width="18ch"
				render={formatDate}
			/>

			<Table.Column
				width="32ch"
				title={txt('global.description')}
				render={renderTextContent}
				ellipsis
			/>

			<Table.Column
				dataIndex="municipalities"
				title={txt('global.municipality')}
				render={renderMunicipalities}
				width="32ch"
				ellipsis
			/>

			<Table.Column
				title={txt('global.duration')}
				render={renderDurationDates}
				align="center"
				width="32ch"
			/>
		</TableStyled>
	);
};

const TableStyled = styled(SharedTable)`
	.duration-container {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-wrap: wrap;
		gap: 2ch;
	}

	.body-content {
		white-space: pre-wrap;
	}
`;
